import { useState, useEffect, useCallback } from "react";
import SuperAdminLayout from "../components/SuperAdminLayout";
import api from "../services/api";

const TABS = ["pending", "approved", "rejected"];

export default function SuperAdminPendingShopsPage() {
  const [shops, setShops]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModal, setRejectModal]     = useState(null);
  const [rejectReason, setRejectReason]   = useState("");
  const [detailModal, setDetailModal]     = useState(null);
  const [toast, setToast]                 = useState(null);
  const [search, setSearch]               = useState("");

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchShops = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/super-admin/shops");
      setShops(res.data);
    } catch {
      showToast("Failed to load businesses.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchShops(); }, [fetchShops]);

  const handleApprove = async (shop) => {
    setActionLoading(`approve-${shop.id}`);
    try {
      await api.patch(`/super-admin/shops/${shop.id}/approve`);
      setShops((prev) =>
        prev.map((s) => s.id === shop.id ? { ...s, status: "approved", is_active: true } : s)
      );
      showToast(`✅ ${shop.name} approved. Trial started.`);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to approve.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleExtendTrial = async (shop) => {
    setActionLoading(`extend-${shop.id}`);
    try {
      await api.patch(`/super-admin/shops/${shop.id}/extend-trial`);
      setShops((prev) =>
        prev.map((s) =>
          s.id === shop.id
            ? { ...s, trial_ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() }
            : s
        )
      );
      showToast(`✅ Trial extended 7 days for ${shop.name}.`);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to extend trial.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setActionLoading(`reject-${rejectModal.id}`);
    try {
      await api.patch(`/super-admin/shops/${rejectModal.id}/reject`, {
        reason: rejectReason,
      });
      setShops((prev) =>
        prev.map((s) =>
          s.id === rejectModal.id
            ? { ...s, status: "rejected", is_active: false, rejection_reason: rejectReason }
            : s
        )
      );
      showToast(`${rejectModal.name} has been rejected.`, "error");
      setRejectModal(null);
      setRejectReason("");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to reject.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const openWhatsApp = (phone) => {
    const clean = phone?.replace(/^0/, "254");
    window.open(`https://wa.me/${clean}`, "_blank");
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-KE", {
          day: "numeric", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        })
      : "—";

  const filtered = shops
    .filter((s) => s.status === activeTab)
    .filter((s) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        s.name?.toLowerCase().includes(q) ||
        s.phone?.includes(q) ||
        s.owner?.name?.toLowerCase().includes(q) ||
        s.business_type?.toLowerCase().includes(q)
      );
    });

  const counts = {
    pending:  shops.filter((s) => s.status === "pending").length,
    approved: shops.filter((s) => s.status === "approved").length,
    rejected: shops.filter((s) => s.status === "rejected").length,
  };

  const Spinner = () => (
    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
    </svg>
  );

  return (
    <SuperAdminLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-sm font-medium ${
          toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
        }`}>
          {toast.type === "error" ? "❌" : "✅"} {toast.message}
        </div>
      )}

      <div className="p-7 space-y-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── HEADER ── */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>
              Business Verification
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Review and approve businesses before they access TumaTrack.
            </p>
          </div>

          {/* Pending alert badge */}
          {counts.pending > 0 && (
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-semibold px-4 py-2 rounded-xl">
              <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
              {counts.pending} business{counts.pending !== 1 ? "es" : ""} awaiting review
            </div>
          )}
        </div>

        {/* ── STATS ROW ── */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Pending",  count: counts.pending,  icon: "⏳", color: "from-amber-500/20 to-amber-600/10",  text: "text-amber-400" },
            { label: "Approved", count: counts.approved, icon: "✅", color: "from-green-500/20 to-green-600/10",  text: "text-green-400" },
            { label: "Rejected", count: counts.rejected, icon: "❌", color: "from-red-500/20 to-red-600/10",      text: "text-red-400" },
          ].map((s) => (
            <div key={s.label} className={`bg-gradient-to-br ${s.color} border border-slate-800 rounded-2xl p-5 cursor-pointer`}
              onClick={() => setActiveTab(s.label.toLowerCase())}>
              <span className="text-xl block mb-2">{s.icon}</span>
              <p className={`text-3xl font-extrabold ${s.text}`} style={{ fontFamily: "'Sora', sans-serif" }}>
                {s.count}
              </p>
              <p className="text-slate-400 text-xs mt-1">{s.label} Businesses</p>
            </div>
          ))}
        </div>

        {/* ── TABS + SEARCH ── */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-xl p-1">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-xs font-semibold capitalize transition-all ${
                  activeTab === tab
                    ? "bg-orange-500 text-white shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                {tab} ({counts[tab]})
              </button>
            ))}
          </div>

          <div className="relative flex-1">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500"
              fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 15.803a7.5 7.5 0 0 0 10.607 0z" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by business name, phone or owner..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <button onClick={fetchShops}
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors border border-slate-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            Refresh
          </button>
        </div>

        {/* ── BUSINESSES LIST ── */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400 gap-3">
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
            Loading businesses...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            <p className="text-4xl mb-3">
              {activeTab === "pending" ? "⏳" : activeTab === "approved" ? "✅" : "❌"}
            </p>
            <p className="font-medium text-slate-400">
              No {activeTab} businesses{search ? " matching your search" : ""}.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((shop) => (
              <div key={shop.id}
                className="bg-slate-900 border border-slate-800 rounded-2xl p-6 hover:border-slate-700 transition-colors">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">

                  {/* Business Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 bg-orange-500/10 border border-orange-500/20 rounded-xl flex items-center justify-center text-lg shrink-0">
                        🏪
                      </div>
                      <div>
                        <p className="text-white font-bold text-base">{shop.name}</p>
                        <p className="text-slate-400 text-xs mt-0.5">{shop.business_type}</p>
                      </div>

                      {/* Status badge */}
                      <span className={`ml-auto md:hidden text-xs font-bold px-2.5 py-1 rounded-full capitalize ${
                        shop.status === "pending"  ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                        : shop.status === "approved" ? "bg-green-500/10 text-green-400 border border-green-500/20"
                        : "bg-red-500/10 text-red-400 border border-red-500/20"
                      }`}>
                        {shop.status}
                      </span>
                    </div>

                    {/* Details grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: "Owner",    value: shop.owner?.name || "—" },
                        { label: "Phone",    value: shop.phone },
                        { label: "Location", value: shop.address },
                        { label: "Registered", value: formatDate(shop.created_at) },
                      ].map((row) => (
                        <div key={row.label}>
                          <p className="text-slate-500 text-xs">{row.label}</p>
                          <p className="text-slate-200 text-xs font-medium mt-0.5 truncate">{row.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Optional fields */}
                    {(shop.registration_number || shop.kra_pin) && (
                      <div className="flex flex-wrap gap-3 mt-3">
                        {shop.registration_number && (
                          <span className="text-xs bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1 rounded-full">
                            📋 Reg: {shop.registration_number}
                          </span>
                        )}
                        {shop.kra_pin && (
                          <span className="text-xs bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1 rounded-full">
                            🏛️ KRA: {shop.kra_pin}
                          </span>
                        )}
                      </div>
                    )}

                    {/* ✅ VERIFICATION SECTION (added) */}
                    {shop.status === "pending" && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-100 rounded-xl space-y-1.5">
                        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
                          🔍 Verify Before Approving
                        </p>
                        {shop.kra_pin && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-amber-600">KRA PIN</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs font-bold text-amber-900 bg-amber-100 px-2 py-0.5 rounded">
                                {shop.kra_pin}
                              </span>
                              <a
                                href={`https://itax.kra.go.ke/KRA-Portal/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:text-blue-600 underline"
                              >
                                Check iTax ↗
                              </a>
                            </div>
                          </div>
                        )}
                        {shop.registration_number && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-amber-600">Registration No.</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs font-bold text-amber-900 bg-amber-100 px-2 py-0.5 rounded">
                                {shop.registration_number}
                              </span>
                              <a
                                href="https://www.ecitizen.go.ke"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:text-blue-600 underline"
                              >
                                Check eCitizen ↗
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Rejection reason */}
                    {shop.status === "rejected" && shop.rejection_reason && (
                      <div className="mt-3 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                        <p className="text-red-400 text-xs font-semibold mb-1">Rejection Reason:</p>
                        <p className="text-red-300 text-xs">{shop.rejection_reason}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-row md:flex-col items-center gap-2 shrink-0">

                    {/* Status badge — desktop */}
                    <span className={`hidden md:inline-flex text-xs font-bold px-2.5 py-1 rounded-full capitalize mb-1 ${
                      shop.status === "pending"  ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                      : shop.status === "approved" ? "bg-green-500/10 text-green-400 border border-green-500/20"
                      : "bg-red-500/10 text-red-400 border border-red-500/20"
                    }`}>
                      {shop.status}
                    </span>

                    {/* View details */}
                    <button
                      onClick={() => setDetailModal(shop)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 transition-colors"
                    >
                      👁 View
                    </button>

                    {/* WhatsApp */}
                    <button
                      onClick={() => openWhatsApp(shop.owner?.phone || shop.phone)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20 transition-colors"
                    >
                      💬 WA
                    </button>

                    {/* Approve (pending only) */}
                    {shop.status === "pending" && (
                      <button
                        onClick={() => handleApprove(shop)}
                        disabled={!!actionLoading}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20 transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {actionLoading === `approve-${shop.id}` ? <Spinner /> : "✅"} Approve
                      </button>
                    )}

                    {/* Reject (pending only) */}
                    {shop.status === "pending" && (
                      <button
                        onClick={() => { setRejectModal(shop); setRejectReason(""); }}
                        disabled={!!actionLoading}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-colors disabled:opacity-50"
                      >
                        ❌ Reject
                      </button>
                    )}

                    {/* Re-approve rejected */}
                    {shop.status === "rejected" && (
                      <button
                        onClick={() => handleApprove(shop)}
                        disabled={!!actionLoading}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20 transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {actionLoading === `approve-${shop.id}` ? <Spinner /> : "↩️"} Re-approve
                      </button>
                    )}

                    {shop.status === "approved" && (
                      <button
                        onClick={() => handleExtendTrial(shop)}
                        disabled={!!actionLoading}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 border border-purple-500/20 transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {actionLoading === `extend-${shop.id}` ? <Spinner /> : "⏳"} Extend Trial
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── REJECT MODAL ── */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-white mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>
              Reject Business
            </h2>
            <p className="text-slate-400 text-sm mb-5">
              You are rejecting <span className="text-white font-semibold">{rejectModal.name}</span>.
              Please provide a reason — this will be shown to the business owner.
            </p>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Unable to verify business registration details. Please resubmit with valid documentation."
              rows={4}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none mb-4"
            />

            {/* Quick reasons */}
            <div className="flex flex-wrap gap-2 mb-5">
              {[
                "Unable to verify business details",
                "Duplicate registration detected",
                "Incomplete information provided",
                "Suspicious activity detected",
              ].map((r) => (
                <button
                  key={r}
                  onClick={() => setRejectReason(r)}
                  className="text-xs bg-slate-800 border border-slate-700 text-slate-300 hover:border-red-500/50 hover:text-red-400 px-3 py-1.5 rounded-full transition-colors"
                >
                  {r}
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setRejectModal(null); setRejectReason(""); }}
                className="flex-1 py-2.5 border border-slate-700 text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || !!actionLoading}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {actionLoading === `reject-${rejectModal?.id}` ? (
                  <><Spinner /> Rejecting...</>
                ) : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DETAIL MODAL ── */}
      {detailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-800 sticky top-0 bg-slate-900 rounded-t-2xl">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>
                Business Details
              </h2>
              <button onClick={() => setDetailModal(null)} className="text-slate-400 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {[
                { label: "Business Name",    value: detailModal.name },
                { label: "Business Type",    value: detailModal.business_type },
                { label: "Location",         value: detailModal.address },
                { label: "Phone",            value: detailModal.phone },
                { label: "Email",            value: detailModal.email || "—" },
                { label: "Registration No.", value: detailModal.registration_number || "Not provided" },
                { label: "KRA PIN",          value: detailModal.kra_pin || "Not provided" },
                { label: "Owner Name",       value: detailModal.owner?.name || "—" },
                { label: "Owner Phone",      value: detailModal.owner?.phone || "—" },
                { label: "Owner Email",      value: detailModal.owner?.email || "—" },
                { label: "Applied On",       value: new Date(detailModal.created_at).toLocaleString("en-KE") },
                { label: "Status",           value: detailModal.status, highlight: true },
              ].map((row) => (
                <div key={row.label} className="flex justify-between items-start gap-4 border-b border-slate-800 pb-3">
                  <span className="text-slate-400 text-sm shrink-0">{row.label}</span>
                  <span className={`text-sm font-medium text-right capitalize ${
                    row.highlight
                      ? detailModal.status === "approved" ? "text-green-400"
                        : detailModal.status === "rejected" ? "text-red-400"
                      : "text-amber-400"
                      : "text-slate-200"
                  }`}>
                    {row.value}
                  </span>
                </div>
              ))}

              {detailModal.rejection_reason && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 text-xs font-semibold mb-1">Rejection Reason</p>
                  <p className="text-red-300 text-sm">{detailModal.rejection_reason}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                {detailModal.status === "pending" && (
                  <>
                    <button
                      onClick={() => { handleApprove(detailModal); setDetailModal(null); }}
                      className="flex-1 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-semibold transition-colors"
                    >
                      ✅ Approve
                    </button>
                    <button
                      onClick={() => { setRejectModal(detailModal); setDetailModal(null); }}
                      className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-colors"
                    >
                      ❌ Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => openWhatsApp(detailModal.owner?.phone || detailModal.phone)}
                  className="flex-1 py-2.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-xl text-sm font-semibold hover:bg-green-500/20 transition-colors"
                >
                  💬 WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
}